import { supabase } from '../supabaseClient';
import { debug } from '../helpers/debug';
import { sanitizeInputByType, sanitizeStructuredInput } from '../helpers/formatHelpers';

export function apiGetImageByKey(key) {
  const safeKey = sanitizeInputByType(key, 'text', { maxLength: 512 }).trim();
  if (!safeKey) throw new Error('apiGetImageByKey requires key');
  return (async () => {
    const { data, error } = await supabase
      .from('images')
      .select('*')
      .eq('key', safeKey)
      .maybeSingle();
    if (error) throw error;
    return data || null;
  })();
}

// Insert a canonical image row after upload
export function apiCreateImage({ docId, key, url = null, uploadedBy = null, metadata = {} } = {}) {
  if (!docId) throw new Error('apiCreateImage requires docId');
  const safeKey = sanitizeInputByType(key, 'text', { maxLength: 512 }).trim();
  if (!safeKey) throw new Error('apiCreateImage requires key');
  const safeUrl = sanitizeInputByType(url, 'text', { maxLength: 2048 }).trim() || null;
  const safeUploadedBy = sanitizeInputByType(uploadedBy, 'identifier', { maxLength: 120 }) || null;

  const payload = {
    doc_id: docId,
    key: safeKey,
    url: safeUrl,
    uploaded_by: safeUploadedBy,
    metadata: sanitizeStructuredInput(metadata || {}, { maxDepth: 8, maxStringLength: 2000 })
  };

  return (async () => {
    try {
      const { data, error } = await supabase
        .from('images')
        .insert([payload])
        .select()
        .single();

      if (error) {
        if (error.code === '23505' || error.status === 409) {
          const existing = await apiGetImageByKey(safeKey).catch(() => null);
          return existing || null;
        }
        throw error;
      }
      return data;
    } catch (err) {
      if (err?.code !== '23505' && err?.status !== 409) {
        debug('[apiCreateImage]', { type: 'warn' }, err);
      }
      return null;
    }
  })();
}

export function apiMarkImageDeleted(key) {
  const safeKey = sanitizeInputByType(key, 'text', { maxLength: 512 }).trim();
  if (!safeKey) throw new Error('apiMarkImageDeleted requires key');
  return (async () => {
    const { data, error } = await supabase
      .from('images')
      .update({ deleted_at: new Date().toISOString() })
      .eq('key', safeKey)
      .select()
      .single();
    if (error) throw error;
    return data;
  })();
}

export function apiCreateVersionImage({ versionId, imageKey } = {}) {
  if (!versionId) throw new Error('apiCreateVersionImage requires versionId');
  const safeImageKey = sanitizeInputByType(imageKey, 'text', { maxLength: 512 }).trim();
  if (!safeImageKey) throw new Error('apiCreateVersionImage requires imageKey');

  const payload = {
    version_id: versionId,
    image_key: safeImageKey
  };

  return (async () => {
    const { data, error } = await supabase
      .from('version_images')
      .insert([payload])
      .select();

    if (error) {
      if (error.code === '23505' || error.status === 409) {
        debug('[apiCreateVersionImage] duplicate conflict ignored', { type: 'warn' }, error);
        return [];
      }
      throw error;
    }
    return data;
  })();
}

export function apiLinkImagesToVersion(versionId, imageKeys = []) {
  if (!versionId) throw new Error('apiLinkImagesToVersion requires versionId');
  if (!Array.isArray(imageKeys) || imageKeys.length === 0) return [];

  const uniqueKeys = Array.from(new Set(
    imageKeys
      .map(key => sanitizeInputByType(key, 'text', { maxLength: 512 }).trim())
      .filter(Boolean)
  ));
  if (uniqueKeys.length === 0) return [];

  const payload = uniqueKeys.map(key => ({ version_id: versionId, image_key: key }));
  return (async () => {
    const { data, error } = await supabase
      .from('version_images')
      .insert(payload)
      .select();

    if (error) {
      if (error.code === '23505' || error.status === 409) {
        debug('[apiLinkImagesToVersion] duplicate conflict ignored', { type: 'warn' }, error);
        return [];
      }
      throw error;
    }
    return data;
  })();
}

export function apiGetImagesForDoc(docId) {
  if (!docId) throw new Error('apiGetImagesForDoc requires docId');
  return (async () => {
    const { data, error } = await supabase
      .from('images')
      .select('*')
      .eq('doc_id', docId)
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data || [];
  })();
}

export async function apiGetVersionImageKeysForVersions(versionIds = []) {
  if (!Array.isArray(versionIds) || versionIds.length === 0) return [];
  const { data, error } = await supabase
    .from('version_images')
    .select('image_key')
    .in('version_id', versionIds);
  if (error) throw error;
  return Array.isArray(data) ? data.map(r => r.image_key) : [];
}

export function apiDeleteImageByKey(key) {
  const safeKey = sanitizeInputByType(key, 'text', { maxLength: 512 }).trim();
  if (!safeKey) throw new Error('apiDeleteImageByKey requires key');
  return (async () => {
    const { error } = await supabase
      .from('images')
      .delete()
      .eq('key', safeKey);
    if (error) throw error;
    return true;
  })();
}
